// src/components/ScenarioHeader/ScenarioHeader.js
import React, { useState, useEffect, useRef } from "react";
import "./ScenarioHeader.css";

const ScenarioHeader = ({
  scenario,
  updateScenario,
  addLane,
  dansMaPoche,
  isCalculating
}) => {
  const [isEditingName, setIsEditingName] = useState(false);
  const [editedName, setEditedName] = useState(scenario?.name || "");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [startingCapital, setStartingCapital] = useState(0);
  const [description, setDescription] = useState("");
  const [notes, setNotes] = useState("");
  const [isExpanded, setIsExpanded] = useState(false);
  const expandedRef = useRef(null);

  useEffect(() => {
    if (scenario) {
      setEditedName(scenario.name || "");
      setStartDate(scenario.startDate ? scenario.startDate.split('T')[0] : "");
      setEndDate(scenario.endDate ? scenario.endDate.split('T')[0] : "");
      setStartingCapital(scenario.startingCapital || 0);
      setDescription(scenario.description || "");
      setNotes(scenario.notes || "");
    }
  }, [scenario]);

  // Close expanded on click outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (expandedRef.current && !expandedRef.current.contains(event.target)) {
        setIsExpanded(false);
      }
    };
    if (isExpanded) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isExpanded]);

  const handleNameSave = () => {
    if (editedName.trim() && editedName !== scenario.name) {
      updateScenario(scenario.id, (current) => ({
        ...current,
        name: editedName.trim()
      }));
    }
    setIsEditingName(false);
  };

  const handleNameKeyDown = (e) => {
    if (e.key === "Enter") {
      handleNameSave();
    } else if (e.key === "Escape") {
      setEditedName(scenario.name || "");
      setIsEditingName(false);
    }
  };

  const handleDateChange = (field, value) => {
    if (field === 'start') {
      setStartDate(value);
      updateScenario(scenario.id, (current) => ({
        ...current,
        startDate: value ? new Date(value).toISOString() : null
      }));
    } else {
      setEndDate(value);
      updateScenario(scenario.id, (current) => ({
        ...current,
        endDate: value ? new Date(value).toISOString() : null
      }));
    }
  };

  const handleCapitalChange = (value) => {
    const capital = parseFloat(value) || 0;
    setStartingCapital(capital);
    updateScenario(scenario.id, (current) => ({
      ...current,
      startingCapital: capital
    }));
  };

  const handleDescriptionChange = (value) => {
    setDescription(value);
    updateScenario(scenario.id, (current) => ({
      ...current,
      description: value
    }));
  };

  const handleNotesChange = (value) => {
    setNotes(value);
    updateScenario(scenario.id, (current) => ({
      ...current,
      notes: value
    }));
  };

  const toggleExpanded = (e) => {
    // Don't toggle if clicking on interactive elements
    if (e.target.tagName === 'INPUT' || e.target.tagName === 'BUTTON' || e.target.closest('button')) {
      return;
    }
    setIsExpanded(!isExpanded);
  };

  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined) return "- €";
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR',
      maximumFractionDigits: 0
    }).format(amount);
  };

  const canAddLane = scenario?.lanes?.length < 4;

  // Get primary individual name
  const primaryIndividualId = scenario?.primaryIndividualId || scenario?.primary_individual_id;
  const primaryIndividual = scenario?.boxes?.find(
    b => b.content === 'Individu' && (b.individual_id === primaryIndividualId || b.id === primaryIndividualId)
  );
  const primaryName = primaryIndividual
    ? `${primaryIndividual.first_name || ''} ${primaryIndividual.last_name || ''}`.trim() || 'Personne'
    : null;

  if (!scenario) return null;

  // dansMaPoche.total already includes startingCapital from ScenarioTabs calculation
  const hasMissingDates = dansMaPoche?.missingDates;
  const totalDansMaPoche = hasMissingDates ? null : (dansMaPoche ? dansMaPoche.total : startingCapital);

  return (
    <div className="scenario-header-container" ref={expandedRef}>
      {/* Main header row - clickable to toggle expand */}
      <div className="scenario-header" onClick={toggleExpanded}>
        {/* Left section: Name and primary person */}
        <div className="header-left">
          {isEditingName ? (
            <input
              type="text"
              className="scenario-name-input"
              value={editedName}
              onChange={(e) => setEditedName(e.target.value)}
              onBlur={handleNameSave}
              onKeyDown={handleNameKeyDown}
              onClick={(e) => e.stopPropagation()}
              autoFocus
            />
          ) : (
            <h2
              className="scenario-name"
              onClick={(e) => {
                e.stopPropagation();
                setIsEditingName(true);
              }}
              title="Cliquer pour modifier"
            >
              {scenario.name || "Nouveau scénario"}
            </h2>
          )}

          {primaryName && (
            <div className="primary-person-badge" title="Personne principale">
              <span className="star">⭐</span>
              <span className="name">{primaryName}</span>
            </div>
          )}
        </div>

        {/* Center section: Capital → Dates → DansMaPoche → Enregistrer */}
        <div className="header-center">
          <div className="header-field capital-field">
            <label>Capital</label>
            <input
              type="number"
              value={startingCapital}
              onChange={(e) => handleCapitalChange(e.target.value)}
              onClick={(e) => e.stopPropagation()}
              placeholder="0"
              min="0"
              step="1000"
            />
          </div>

          <div className="header-field date-field">
            <label>Début</label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => handleDateChange('start', e.target.value)}
              onClick={(e) => e.stopPropagation()}
            />
          </div>

          <div className="header-field date-field">
            <label>Fin</label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => handleDateChange('end', e.target.value)}
              onClick={(e) => e.stopPropagation()}
            />
          </div>

          <div
            className={`dans-ma-poche ${isCalculating ? 'calculating' : ''} ${hasMissingDates ? 'missing-dates' : ''}`}
            title={hasMissingDates ? "Définissez les dates du scénario" : "Résultat net"}
          >
            <div className="dans-ma-poche-label">DansMaPoche</div>
            <div className="dans-ma-poche-value">
              {isCalculating ? '...' : (hasMissingDates ? '-- € *' : formatCurrency(totalDansMaPoche))}
            </div>
            {hasMissingDates && <div className="missing-dates-hint">Dates requises</div>}
          </div>
        </div>

        {/* Right section: Fold arrow + Niveau (alone) */}
        <div className="header-right">
          <button
            className="expand-btn"
            onClick={(e) => {
              e.stopPropagation();
              setIsExpanded(!isExpanded);
            }}
            title={isExpanded ? "Réduire" : "Voir les détails"}
          >
            {isExpanded ? '▲' : '▼'}
          </button>

          <button
            className="add-level-btn"
            onClick={(e) => {
              e.stopPropagation();
              addLane();
            }}
            disabled={!canAddLane}
            title={canAddLane ? "Ajouter un niveau" : "Maximum 4 niveaux"}
          >
            + Niveau
          </button>
        </div>
      </div>

      {/* Expanded overlay section */}
      {isExpanded && (
        <div className="scenario-header-expanded">
          {/* Description and Notes */}
          <div className="expanded-fields">
            <div className="field-group">
              <label>Description</label>
              <input
                type="text"
                value={description}
                onChange={(e) => handleDescriptionChange(e.target.value)}
                placeholder="Objectif ou contexte du scénario..."
              />
            </div>
            <div className="field-group">
              <label>Notes</label>
              <textarea
                value={notes}
                onChange={(e) => handleNotesChange(e.target.value)}
                placeholder="Notes libres..."
                rows={2}
              />
            </div>
          </div>

          {/* Detailed breakdown - compact */}
          <div className="expanded-breakdown">
            <div className="breakdown-section">
              <h4>Revenus</h4>
              {dansMaPoche?.details ? (
                <div className="breakdown-items">
                  {dansMaPoche.details.remuneration > 0 && (
                    <span className="item">Rém. {formatCurrency(dansMaPoche.details.remuneration)}</span>
                  )}
                  {dansMaPoche.details.dividendes > 0 && (
                    <span className="item">Div. {formatCurrency(dansMaPoche.details.dividendes)}</span>
                  )}
                  {dansMaPoche.details.salaires > 0 && (
                    <span className="item">Sal. {formatCurrency(dansMaPoche.details.salaires)}</span>
                  )}
                  <span className="item total">= {formatCurrency(dansMaPoche.details.totalRevenus || 0)}</span>
                </div>
              ) : (
                <span className="no-data">-</span>
              )}
            </div>

            <div className="breakdown-section">
              <h4>Fiscalité</h4>
              {dansMaPoche?.details ? (
                <div className="breakdown-items">
                  {dansMaPoche.details.ir > 0 && (
                    <span className="item negative">IR -{formatCurrency(dansMaPoche.details.ir)}</span>
                  )}
                  {dansMaPoche.details.pfu > 0 && (
                    <span className="item negative">PFU -{formatCurrency(dansMaPoche.details.pfu)}</span>
                  )}
                  {dansMaPoche.details.ps > 0 && (
                    <span className="item negative">PS -{formatCurrency(dansMaPoche.details.ps)}</span>
                  )}
                  <span className="item total negative">= -{formatCurrency(dansMaPoche.details.totalImpots || 0)}</span>
                </div>
              ) : (
                <span className="no-data">-</span>
              )}
            </div>

            <div className="breakdown-section result">
              <h4>Résultat</h4>
              <div className="result-value">{formatCurrency(totalDansMaPoche)}</div>
              {dansMaPoche?.details?.tauxEffectif && (
                <span className="rate">Taux: {dansMaPoche.details.tauxEffectif}%</span>
              )}
              {startingCapital > 0 && (
                <span className="capital-note">dont {formatCurrency(startingCapital)} capital</span>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ScenarioHeader;
